/**
 * A PIN box that the browser will not try to be helpful about.
 *
 * `autocomplete="off"` is not enough and never has been: Chrome ignores it on
 * password fields, on the reasonable theory that most sites that use it are
 * wrong. Here it is not wrong. A dropdown offering a saved value is a hint, and
 * the person it would be hinting to is a ten-year-old standing in front of the
 * screen their parents' controls are behind.
 *
 * So the field says three things at once:
 *
 *   - `one-time-code` is the one autocomplete value Chrome does not answer with
 *     saved passwords. It is the closest honest description anyway: a short
 *     number typed to get past a door, not a credential worth keeping.
 *   - a `name` that no heuristic will match against a stored entry.
 *   - the opt-outs the third-party password managers read.
 *
 * None of it is a security boundary — the hash and the lockout are. This is
 * about not putting the answer on the screen next to the question.
 */
export function PinField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <>
      <label className="mb-1 block text-sm font-bold" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        name={`clave-${id}`}
        className="border-rule bg-surface mb-4 w-full rounded-lg border px-4 text-center text-2xl tracking-[0.6em]"
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore=""
        data-bwignore="true"
        data-form-type="other"
        maxLength={8}
        value={value}
        onChange={(e) => {
          onChange(e.target.value.replace(/\D/g, ''))
        }}
      />
    </>
  )
}
